// ============================================================
// INTAKE FORM — Data Types & Utilities
// Design: Quiet Reverence — warm cream editorial
// ============================================================

export type VideoTier = 'slideshow' | 'tribute' | 'legacy';
export type VideoTone = 'celebration' | 'reverent' | 'humorous';
export type StylePreset =
  | 'quiet_reverence'
  | 'golden_legacy'
  | 'modern_minimal'
  | 'natures_rest'
  | 'the_architect'
  | 'noir_spirit'
  | 'ocean_drift'
  | 'vintage_polar'
  | 'spirit_of_aloha'
  | 'digital_soul'
  | 'auto';

export interface DeceasedInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  dateOfPassing: string;
  tone: VideoTone;
  tier: VideoTier;
  stylePreset: StylePreset;
  tagline: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  musicStyle?: string;
  musicTrack?: string;       // R2 key of the selected library track
  musicTrackTitle?: string;  // Display title of the selected library track
}

export type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';

export interface PhotoAsset {
  id: string;
  file?: File;          // present until page reload; undefined after restore from localStorage
  previewUrl: string;   // object URL (local) or placeholder after restore
  caption: string;
  r2Key?: string;       // set once uploaded directly to R2
  objectKey?: string;   // alias for r2Key used by upload pipeline
  uploadStatus?: UploadStatus;
  uploadError?: string;
}

export interface VideoAsset {
  id: string;
  file?: File;
  previewUrl: string;
  caption: string;
  r2Key?: string;
  objectKey?: string;   // alias for r2Key used by upload pipeline
  uploadStatus?: UploadStatus;
  uploadError?: string;
}

export interface AudioAsset {
  id: string;
  file?: File;
  name: string;
  durationLabel: string;
  type: 'voicemail' | 'music';
  r2Key?: string;
  objectKey?: string;   // alias for r2Key used by upload pipeline
  uploadStatus?: UploadStatus;
  uploadError?: string;
}

export interface IntakeFormState {
  step: number;
  deceased: DeceasedInfo;
  photos: PhotoAsset[];
  videos: VideoAsset[];
  audio: AudioAsset[];
  additionalNotes: string;
  submitting: boolean;
  submitted: boolean;
  projectId: string;
}

export const INITIAL_STATE: IntakeFormState = {
  step: 1,
  deceased: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    dateOfPassing: '',
    tone: 'reverent',
    tier: 'tribute',
    stylePreset: 'auto',
    tagline: '',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
  },
  photos: [],
  videos: [],
  audio: [],
  additionalNotes: '',
  submitting: false,
  submitted: false,
  projectId: '',
};

export const TIER_INFO: Record<VideoTier, { label: string; price: string; description: string; photoLimit: number }> = {
  slideshow: {
    label: 'The Slideshow',
    price: '$49',
    description: 'Up to 50 photos, 1 background music track. A beautiful, sequential tribute.',
    photoLimit: 50,
  },
  tribute: {
    label: 'The Tribute',
    price: '$149',
    description: 'Up to 150 photos, 3 music tracks, voice tributes. AI-matched photos to spoken words.',
    photoLimit: 150,
  },
  legacy: {
    label: 'The Legacy',
    price: '$249',
    description: 'Up to 300 photos, unlimited music, unlimited voice tributes. Priority rendering.',
    photoLimit: 300,
  },
};

export const STYLE_PRESET_INFO: Record<StylePreset, { label: string; description: string; thumbnailUrl: string; accentHex: string }> = {
  auto: {
    label: 'Auto-Select',
    description: 'We choose the best style based on your tone and music selections.',
    thumbnailUrl: '',
    accentHex: '#888888',
  },
  quiet_reverence: {
    label: 'Quiet Reverence',
    description: 'Aged parchment, muted sepia. Traditional and dignified.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_quiet_reverence_3447df3c.png',
    accentHex: '#C4A882',
  },
  golden_legacy: {
    label: 'Golden Legacy',
    description: 'Warm golden bokeh, amber light. Celebratory and warm.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_golden_legacy_e932608a.png',
    accentHex: '#F0C040',
  },
  modern_minimal: {
    label: 'Modern Minimal',
    description: 'Clean white-to-charcoal gradient. Contemporary and refined.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_modern_minimal_ea7d4e6e.png',
    accentHex: '#AAAAAA',
  },
  natures_rest: {
    label: "Nature's Rest",
    description: 'Soft forest bokeh, morning light. Peaceful and natural.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_natures_rest_e0010788.png',
    accentHex: '#8FBC8F',
  },
  the_architect: {
    label: 'The Architect',
    description: 'Blueprint grid, deep navy. Precise and intellectual.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_the_architect_db7fa3f0.png',
    accentHex: '#0099CC',
  },
  noir_spirit: {
    label: 'Noir Spirit',
    description: 'Deep black, silver film grain. Dramatic and artistic.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_noir_spirit_f16125dc.png',
    accentHex: '#C0C0C0',
  },
  ocean_drift: {
    label: 'Ocean Drift',
    description: 'Teal ocean horizon, light leaks. Coastal and serene.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_ocean_drift_84435762.png',
    accentHex: '#40B4C4',
  },
  vintage_polar: {
    label: 'Vintage Polar',
    description: 'Faded Polaroid aesthetic, warm overexposed center. Nostalgic.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_vintage_polar_fdcfbe94.png',
    accentHex: '#C4956A',
  },
  spirit_of_aloha: {
    label: 'Spirit of Aloha',
    description: 'Lush tropical Hawaii, golden hour Pacific light.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_spirit_of_aloha_2b411b14.png',
    accentHex: '#FF6B6B',
  },
  digital_soul: {
    label: 'Digital Soul',
    description: 'Vibrant purple/cyan light trails. Energetic and modern.',
    thumbnailUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/style_plate_digital_soul_20b3db54.png',
    accentHex: '#AA44FF',
  },
};

export const TONE_INFO: Record<VideoTone, { label: string; description: string }> = {
  celebration: { label: 'Celebration of Life', description: 'Uplifting, joyful — honoring a life fully lived' },
  reverent: { label: 'Reverent & Dignified', description: 'Quiet, solemn — a tender farewell' },
  humorous: { label: 'Warm & Humorous', description: 'Light-hearted — the way they would have wanted it' },
};

export const TOTAL_STEPS = 5;

export const STEP_LABELS = [
  'Their Story',
  'Photos',
  'Video Clips',
  'Voice Tributes',
  'Review & Submit',
];

export function generateProjectId(firstName: string, lastName: string): string {
  const date = new Date();
  const stamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  const name = `${lastName.replace(/\s+/g, '')}_${firstName.replace(/\s+/g, '')}`.toUpperCase().substring(0, 16);
  return `${name}_${stamp}_${rand}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const ACCEPTED_PHOTO_TYPES = 'image/jpeg,image/jpg,image/png,image/heic,image/heif,image/webp';
export const ACCEPTED_VIDEO_TYPES = 'video/mp4,video/quicktime,video/mov,video/mpeg,video/x-msvideo';
export const ACCEPTED_AUDIO_TYPES = 'audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/m4a,audio/aac';
